import { useContext, useMemo } from "react";

import { MyAccountContext } from "@/models/StateProviders/myAccountProvider";
import { transcriptionForContent } from "@/store/feedSelectors";
import { idx, rowsByIndex, store } from "@/store/store";
import type { AccountEvent } from "@/store/types";
import { useCollectionsCompute } from "@/store/useCollectionsCompute";
import type { DriverRow } from "@/types/driver";
import { targetCodecForBrowser } from "@/utils/audio";
import { feedFold, feedItemFold } from "./acknowledgements";
import { buildDispatchThread, type ThreadEntry, type ThreadSources } from "./dispatchThread";

/** Account names for the thread's receipts and events, read from the store. */
const nameForAccount: ThreadSources["nameFor"] = (accountId) => {
  if (!accountId) return null;
  const account = store.get("account", accountId);
  return account ? { name: account.name ?? null, bot: account.bot === true } : null;
};

/** A membership's name for an acknowledgement's actor: the account behind it, read from the store. */
function nameForMembership(membershipId: string | null | undefined): string | null {
  if (!membershipId) return null;
  const membership = store.get("workspaceMembership", membershipId);
  return nameForAccount(membership?.accountId)?.name ?? null;
}

/** The collections the thread reads, for `useCollectionsCompute`. */
export const threadCollections = [
  "item",
  "transcription",
  "audioEncoding",
  "file",
  "link",
  "accountEvent",
  "account",
  "workspaceMembership",
  "tmsEvent",
  "feed",
  "feedAcknowledgement",
  "feedItemAcknowledgement",
] as const;

const NO_ENTRIES: ThreadEntry[] = [];

/** The driver whose channel the thread shows: the account whose receipts count, the membership whose events show. */
export type ThreadDriver = Pick<DriverRow, "accountId" | "workspaceMembershipId">;

/**
 * The store-backed sources the pure thread builder reads, for the feed named.
 *
 * The feed's own row joins for `loadedFirstPage`, which flips after the thread's opening fetch and lifts the events'
 * floor, so `feed` is among the collections the thread recomputes on.
 *
 * The driver's TMS events always join: the thread is the Dispatch page's, and a workspace on that page has its events
 * loaded. Without a driver there is no membership to read them for.
 */
export function threadSourcesFor(
  feedId: string,
  driver: ThreadDriver | null,
  language: string,
  now: number,
  messagesRequireAcknowledgement = true,
) {
  const codec = targetCodecForBrowser();
  const items = [...rowsByIndex("item", idx.itemsByFeed, feedId)];
  const eventsByItem = new Map<string, AccountEvent[]>();
  for (const item of items) {
    eventsByItem.set(item.id, [...rowsByIndex("accountEvent", idx.accountEventsByItem, item.id)]);
  }
  const sources: ThreadSources = {
    items,
    driverAccountId: driver?.accountId ?? null,
    acknowledgements: { feed: feedFold(feedId), itemFold: feedItemFold },
    actorNameFor: nameForMembership,
    messagesRequireAcknowledgement,
    tmsEvents: driver ? rowsByIndex("tmsEvent", idx.tmsEventsByMembership, driver.workspaceMembershipId) : [],
    historyExhausted: store.get("feed", feedId)?.loadedFirstPage === true,
    eventsByItem,
    textFor: (contentId) => transcriptionForContent(contentId, language)?.transcriptionContent ?? null,
    audioSecondsFor: (contentId) => {
      const encodings = [...rowsByIndex("audioEncoding", idx.audioEncodingsByContent, contentId)];
      const playable = encodings.find((encoding) => encoding.codec === codec) ?? encodings[0];
      const seconds = playable ? Number(playable.duration) : Number.NaN;
      return Number.isFinite(seconds) ? seconds : null;
    },
    filesFor: (contentId) => [...rowsByIndex("file", idx.filesByContent, contentId)],
    linksFor: (contentId) => [...rowsByIndex("link", idx.linksByContent, contentId)],
    nameFor: nameForAccount,
    now,
  };
  return sources;
}

/**
 * The thread a driver's History pane shows, recomputed as items, transcriptions, audio, events, accounts, and TMS
 * events land. `now` moves the acknowledgement lookback; hand it a minute clock.
 */
export function useDispatchThread(
  feedId: string | null,
  driver: ThreadDriver | null,
  now: number,
  messagesRequireAcknowledgement = true,
): ThreadEntry[] {
  const { myAccount } = useContext(MyAccountContext);
  const language = myAccount?.preferredLanguage ?? "en";
  const accountId = driver?.accountId ?? null;
  const membershipId = driver?.workspaceMembershipId ?? null;
  const computed = useCollectionsCompute(
    threadCollections,
    () =>
      feedId
        ? buildDispatchThread(threadSourcesFor(feedId, driver, language, now, messagesRequireAcknowledgement))
        : null,
    [feedId, accountId, membershipId, language, now, messagesRequireAcknowledgement],
    "Dispatch:thread",
  );
  return useMemo(() => computed ?? NO_ENTRIES, [computed]);
}
